import { useCallback, useMemo, useRef, useState } from 'react';
import { FieldError, useFormContext, useFormState } from 'react-hook-form';
import _get from 'lodash/get';
import _noop from 'lodash/noop';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  CompanyPlanUpgradeFeatureEnum,
} from 'graphql-common';
import { FormRef } from '@lib/components/ReactHookForm/types';
import getErrorStr from '@lib/components/ReactHookForm/getErrorStr';
import { PlanState } from '@lib/enums/plan';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import useDocumentActions from 'components/FormTemplateBuilder/utils/useDocumentActions';
import useFormDocumentsMap from 'components/FormTemplateBuilder/utils/FormDocumentsMapContext';
import getSelectedDocValues from './getSelectedDocValues';
import getOptionsFromSelectedIds from './getOptionsFromSelectedIds';
import getOptionsWithRequiredToReadChanged from './getOptionsWithRequiredToReadChanged';
import getOptionsWithRemovedIndex from './getOptionsWithRemovedIndex';

export const DOCUMENT_SELECTIONS_FIELD = '__documentSelections';

interface Props {
  parentName: string;
}

export default function useActions(props: Props) {
  const { parentName } = props;
  const { clearErrors, control, watch, setValue } = useFormContext();
  const { errors } = useFormState({ control, name: parentName });
  const errorMessage = getErrorStr(
    _get(errors, parentName) as FieldError | undefined,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { onViewDocument, onVersionNotes, onDetails, renderDocumentModals } =
    useDocumentActions();

  const {
    getUserAccessByCompany,
    handleOpenPlanModal,
    isFullAdmin,
  }: ContextProps = useOutletContext();

  const isViewLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;

  const onUpgradePlan = useCallback(() => {
    handleOpenPlanModal(PlanState.Locked, [
      CompanyPlanUpgradeFeatureEnum.Documentation,
    ]);
  }, [handleOpenPlanModal]);

  const watchedOptions = watch(parentName);
  const currentOptions = useMemo(() => watchedOptions || [], [watchedOptions]);

  const selectedDocValues = useMemo(
    () => getSelectedDocValues(currentOptions),
    [currentOptions],
  );

  const docIds = useMemo(
    () => selectedDocValues.map((v) => v.value),
    [selectedDocValues],
  );

  const { documentsMap, loading } = useFormDocumentsMap(docIds);

  const syntheticFormRef = useRef<FormRef | undefined>(undefined);
  syntheticFormRef.current = {
    values: { [DOCUMENT_SELECTIONS_FIELD]: selectedDocValues },
    errors: {},
    reset: _noop as FormRef['reset'],
    setValue: _noop as unknown as FormRef['setValue'],
    isDirty: false,
  };

  const onOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const onCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const onSelect = useCallback(
    (selectedIds: string[]) => {
      const newOptions = getOptionsFromSelectedIds(selectedIds, currentOptions);
      clearErrors(parentName);
      setValue(parentName, newOptions, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      setIsModalOpen(false);
    },
    [clearErrors, currentOptions, parentName, setValue],
  );

  const onRemoveDocument = useCallback(
    (index: number) => {
      const filtered = getOptionsWithRemovedIndex(currentOptions, index);
      clearErrors(parentName);
      setValue(parentName, filtered, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [clearErrors, currentOptions, parentName, setValue],
  );

  const onRequiredToReadChange = useCallback(
    (index: number, option: { value: boolean } | null) => {
      if (!option) return;
      const updated = getOptionsWithRequiredToReadChanged(
        currentOptions,
        index,
        option.value,
      );
      setValue(parentName, updated, { shouldDirty: true });
    },
    [currentOptions, parentName, setValue],
  );

  return {
    errorMessage,
    isModalOpen,
    isViewLocked,
    isFullAdmin,
    currentOptions,
    selectedDocValues,
    documentsMap,
    loading,
    syntheticFormRef,
    onOpenModal,
    onCloseModal,
    onSelect,
    onRemoveDocument,
    onRequiredToReadChange,
    onUpgradePlan,
    onViewDocument,
    onVersionNotes,
    onDetails,
    renderDocumentModals,
  };
}
